<script setup lang="ts">
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import Avatar from 'vue3-avatar';
import { ref } from 'vue';
import { API_URL } from '@idem/common/constantes';
import { armarUrl } from '@idem/common/armar-url';

const apiBaseUrl = API_URL;

const $route = useRoute();
const router = useRouter();

const actividad = ref((await axios.get(`${apiBaseUrl}/actividades/${$route.params.id}`)).data);

const registrar = async () => {
  try {
    await axios.post(`${apiBaseUrl}/actividades/${actividad.value.id}/registro`);
    alert('Te has registrado en la actividad');
    actividad.value.participantes = (
      await axios.get(`${apiBaseUrl}/actividades/${$route.params.id}`)
    ).data.participantes;
    actividad.value.registrado = true;
  } catch (e: any) {
    if (e.response?.status === 401) {
      await router.push(`/login`);
    } else {
      alert(e.response?.data?.message || 'Ocurrió un error');
    }
  }
};

const cancelarRegistro = async () => {
  try {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await axios.delete(`${apiBaseUrl}/actividades/${actividad.value?.id}/registro`, {
      headers,
      data: { idActividad: actividad.value.id },
    });
    alert('Has cancelado tu registro en la actividad');
    actividad.value.participantes = (
      await axios.get(`${apiBaseUrl}/actividades/${$route.params.id}`)
    ).data.participantes;
    actividad.value.registrado = false;
  } catch (e: any) {
    alert(e.response?.data?.message || 'Ocurrió un error');
  }
};

const sanitizeUrl = url => {
  if (!/https?:\/\//.test(url)) {
    return `https://${url}`;
  }
  return url;
};
</script>

<template>
  <section class="mx-auto max-w-3xl px-4 py-10">
    <router-link
      to="/actividades/lista"
      class="mb-6 inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        class="h-4 w-4"
      >
        <path
          fill-rule="evenodd"
          d="M7.707 14.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5A1 1 0 018.707 4.293L5.414 7.586H16a1 1 0 110 2H5.414l3.293 3.293a1 1 0 010 1.414z"
          clip-rule="evenodd"
        />
      </svg>
      Volver a la lista
    </router-link>

    <div v-if="actividad" class="rounded-2xl border border-gray-200 bg-white shadow">
      <div class="p-6 sm:p-8">
        <h1 class="text-2xl font-bold tracking-tight text-gray-900">
          {{ actividad.nombre }}
        </h1>
        <h2 class="mt-2 text-sm font-medium text-gray-500" data-testid="descripcion-actividad">
          {{ actividad.descripcion }}
        </h2>
        <div class="mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-600">
          <span
            class="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1 font-medium text-green-700"
            data-testid="fecha-actividad"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="h-4 w-4"
            >
              <path
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1z"
              />
              <path
                fill-rule="evenodd"
                d="M18 8H2v7a2 2 0 002 2h12a2 2 0 002-2V8zM7 12a1 1 0 100-2 1 1 0 000 2zm3-1a1 1 0 011-1h5a1 1 0 110 2h-5a1 1 0 01-1-1z"
                clip-rule="evenodd"
              />
            </svg>
            {{ actividad.fecha }}
          </span>
          <span
            class="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 font-medium text-teal-700"
            data-testid="hora-actividad"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="h-4 w-4"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm.5-12a.5.5 0 00-1 0v4a.5.5 0 00.146.354l2.5 2.5a.5.5 0 10.708-.708L10.5 9.793V6z"
                clip-rule="evenodd"
              />
            </svg>
            {{ actividad.hora }}
          </span>
          <img v-if="actividad.foto" :src="armarUrl(actividad.foto)" alt="foto" />
        </div>

        <dl class="mt-6 divide-y divide-gray-100">
          <div class="flex items-start justify-between py-3">
            <dt class="text-sm font-medium text-gray-500">Dirección</dt>
            <dd class="max-w-sm text-right text-sm text-gray-900" data-testid="direccion-actividad">
              {{ actividad.direccion }}
            </dd>
          </div>
          <div class="flex items-start justify-between py-3">
            <dt class="text-sm font-medium text-gray-500">Empresa</dt>
            <dd
              class="max-w-sm text-right text-sm text-gray-900"
              data-testid="restaurante-actividad"
            >
              {{ actividad.nombreEmpresa }}
            </dd>
          </div>
          <div class="flex items-start justify-between py-3">
            <dt class="text-sm font-medium text-gray-500">Link</dt>
            <dd
              class="max-w-sm text-right text-sm text-gray-900"
              data-testid="restaurante-actividad"
            >
              <a :href="sanitizeUrl(actividad.link)" target="_blank" rel="noreferrer">{{
                actividad.link
              }}</a>
            </dd>
          </div>
        </dl>
        <button
          v-if="!actividad?.registrado"
          @click="registrar"
          type="button"
          class="mt-6 inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
          data-testid="boton-registro"
        >
          Registrarme
        </button>
        <button
          v-if="actividad?.registrado"
          @click="cancelarRegistro"
          type="button"
          class="mt-3 inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          data-testid="boton-cancelar-registro"
        >
          Cancelar registro
        </button>
      </div>

      <h1 class="pb-5 pl-2" v-if="actividad.participantes" data-testid="titulo-participantes">
        Participantes:
      </h1>
      <div class="flex flex-wrap gap-3 pl-5">
        <div
          class="mx-1"
          v-for="(participante, index) in actividad.participantes"
          :key="index"
          style="height: 140px; width: 100px"
          data-testid="participante-actividad"
        >
          <avatar
            :imageSrc="`${apiBaseUrl}/fotos/${participante.foto}`"
            :name="`${participante.nombre}`"
            :customAvatarStyle="{ height: '100px', width: '100px' }"
          ></avatar>
          <div class="p-2 text-center">{{ participante.nombre }}</div>
        </div>
      </div>
    </div>

    <div
      v-if="!actividad"
      class="rounded-lg border border-dashed border-gray-300 p-10 text-center"
      data-testid="no-actividad"
    >
      <p class="text-gray-500">No encontramos los detalles de esta actividad.</p>
      <router-link
        to="/actividades/lista"
        class="mt-4 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
      >
        Volver a actividades
      </router-link>
    </div>
  </section>
</template>

<style scoped></style>
